import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, ExternalLink, Share2, Download, Eye, EyeOff, Printer, Plus, X } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Field, SaveBtn, Toggle } from "./ui";
import { DAY_LABELS, DAY_ORDER, slugify } from "@/lib/format";

const PAYMENT_LABELS = { pix: "Pix", dinheiro: "Dinheiro", cartao: "Cartão" };

// Normalize any stored day (old {open,close,closed} or new {closed,intervals})
// into the editable { closed, intervals:[{open,close}] } shape.
function normDay(d) {
  if (!d) return { closed: false, intervals: [{ open: "18:00", close: "23:00" }] };
  if (Array.isArray(d.intervals)) {
    const ivs = d.intervals.filter((i) => i && i.open && i.close).map((i) => ({ open: i.open, close: i.close }));
    return { closed: !!d.closed, intervals: ivs.length ? ivs : [{ open: "18:00", close: "23:00" }] };
  }
  return { closed: !!d.closed, intervals: [{ open: d.open || "18:00", close: d.close || "23:00" }] };
}
const cloneDay = (d) => ({ closed: d.closed, intervals: d.intervals.map((i) => ({ ...i })) });

// ---------- Atendimento ----------
export function ServiceModule({ rid, data, reload }) {
  const [hours, setHours] = useState(() => {
    const h = {};
    DAY_ORDER.forEach((d) => { h[d] = normDay(data.restaurant.opening_hours?.[d]); });
    return h;
  });
  const [accepting, setAccepting] = useState(data.restaurant.accepting_orders !== false);
  const [pay, setPay] = useState(() => {
    const p = data.restaurant.payment_methods || {};
    return { pix: p.pix !== false, dinheiro: p.dinheiro !== false, cartao: p.cartao !== false };
  });
  const [loading, setLoading] = useState(false);
  const [copyFrom, setCopyFrom] = useState("mon");

  const setInterval = (d, idx, k, v) => setHours((h) => {
    const day = cloneDay(h[d]); day.intervals[idx] = { ...day.intervals[idx], [k]: v }; return { ...h, [d]: day };
  });
  const addInterval = (d) => setHours((h) => {
    const day = cloneDay(h[d]); day.intervals.push({ open: "18:00", close: "23:00" }); return { ...h, [d]: day };
  });
  const removeInterval = (d, idx) => setHours((h) => {
    const day = cloneDay(h[d]); day.intervals.splice(idx, 1);
    if (!day.intervals.length) day.intervals = [{ open: "18:00", close: "23:00" }];
    return { ...h, [d]: day };
  });
  const toggleClosed = (d) => setHours((h) => ({ ...h, [d]: { ...cloneDay(h[d]), closed: !h[d].closed } }));

  const applyRange = (days, src) => setHours((h) => { const n = { ...h }; days.forEach((d) => { n[d] = cloneDay(src); }); return n; });
  const all = () => applyRange(DAY_ORDER, hours[copyFrom]);
  const weekdays = () => applyRange(["mon", "tue", "wed", "thu", "fri"], hours[copyFrom]);
  const weekend = () => applyRange(["sat", "sun"], hours[copyFrom]);
  const allOpen = () => setHours((h) => Object.fromEntries(DAY_ORDER.map((d) => [d, { ...cloneDay(h[d]), closed: false }])));
  const allClosed = () => setHours((h) => Object.fromEntries(DAY_ORDER.map((d) => [d, { ...cloneDay(h[d]), closed: true }])));
  const all24 = () => setHours((h) => Object.fromEntries(DAY_ORDER.map((d) => [d, { closed: false, intervals: [{ open: "00:00", close: "23:59" }] }])));

  const togglePay = (k) => setPay((p) => ({ ...p, [k]: !p[k] }));

  const save = async () => {
    if (!Object.values(pay).some(Boolean)) return toast.error("Mantenha pelo menos uma forma de pagamento ativa.");
    setLoading(true);
    try { await api.put(`/restaurants/${rid}`, { opening_hours: hours, accepting_orders: accepting, payment_methods: pay }); toast.success("Atendimento atualizado"); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  return (
    <div className="space-y-5" data-testid="service-module">
      <Card title="Aceitando pedidos" desc="Se pausado, o cardápio continua navegável mas bloqueia o envio de pedidos.">
        <Toggle checked={accepting} onChange={setAccepting} tid="svc-accepting" label={accepting ? "Recebendo pedidos" : "Pedidos pausados"} />
      </Card>

      <Card title="Formas de pagamento" desc="Escolha quais formas seu restaurante aceita. Só as ativas aparecem no checkout.">
        <div className="space-y-3">
          {Object.keys(PAYMENT_LABELS).map((k) => (
            <div key={k} className="flex items-center justify-between">
              <span className="text-sm">{PAYMENT_LABELS[k]}</span>
              <Toggle checked={pay[k]} onChange={() => togglePay(k)} tid={`svc-pay-${k}`} />
            </div>
          ))}
        </div>
        {!Object.values(pay).some(Boolean) && <p className="text-xs text-rose-400 mt-3" data-testid="svc-pay-warning">Mantenha pelo menos uma forma de pagamento ativa.</p>}
      </Card>

      <Card title="Ações rápidas">
        <div className="flex items-center gap-2 mb-3 text-sm">
          <span className="text-zinc-400 text-xs">Copiar de</span>
          <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)} data-testid="svc-copyfrom" className="rounded-lg bg-white/[0.04] border border-white/10 px-2 py-1.5 text-sm outline-none">
            {DAY_ORDER.map((d) => <option key={d} value={d} className="bg-zinc-900">{DAY_LABELS[d]}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-2">
          <Bulk onClick={all} tid="svc-apply-all">Aplicar para todos</Bulk>
          <Bulk onClick={weekdays} tid="svc-weekdays">Seg–Sex</Bulk>
          <Bulk onClick={weekend} tid="svc-weekend">Fim de semana</Bulk>
          <Bulk onClick={allOpen} tid="svc-allopen">Todos abertos</Bulk>
          <Bulk onClick={allClosed} tid="svc-allclosed">Todos fechados</Bulk>
          <Bulk onClick={all24} tid="svc-24h">24 horas</Bulk>
        </div>
      </Card>

      <Card title="Horários de funcionamento" desc="Adicione mais de um turno por dia. O status Aberto/Fechado é detectado automaticamente.">
        <div className="space-y-2.5">
          {DAY_ORDER.map((d) => (
            <div key={d} className="rounded-xl bg-white/[0.02] border border-white/10 p-3" data-testid={`svc-day-${d}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold">{DAY_LABELS[d]}</span>
                <button onClick={() => toggleClosed(d)} data-testid={`svc-toggle-${d}`} className={`text-xs font-semibold px-3 py-1.5 rounded-full shrink-0 ${hours[d].closed ? "bg-white/10 text-zinc-400" : "bg-emerald-500/15 text-emerald-300"}`}>{hours[d].closed ? "Fechado" : "Aberto"}</button>
              </div>
              {!hours[d].closed && (
                <div className="mt-2.5 space-y-2">
                  {hours[d].intervals.map((iv, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input type="time" value={iv.open} onChange={(e) => setInterval(d, idx, "open", e.target.value)} data-testid={`svc-from-${d}-${idx}`} className="flex-1 min-w-0 rounded-lg bg-white/[0.04] border border-white/10 px-2 py-1.5 text-sm outline-none" />
                      <span className="text-zinc-500 text-xs shrink-0">até</span>
                      <input type="time" value={iv.close} onChange={(e) => setInterval(d, idx, "close", e.target.value)} data-testid={`svc-to-${d}-${idx}`} className="flex-1 min-w-0 rounded-lg bg-white/[0.04] border border-white/10 px-2 py-1.5 text-sm outline-none" />
                      {hours[d].intervals.length > 1 && (
                        <button onClick={() => removeInterval(d, idx)} data-testid={`svc-remove-${d}-${idx}`} className="shrink-0 h-8 w-8 rounded-lg bg-white/5 hover:bg-rose-500/20 hover:text-rose-300 flex items-center justify-center text-zinc-400"><X size={15} /></button>
                      )}
                    </div>
                  ))}
                  <button onClick={() => addInterval(d)} data-testid={`svc-add-${d}`} className="flex items-center gap-1 text-xs font-semibold text-[#FF8A3D] hover:text-[#FF6B00] mt-1"><Plus size={14} /> Adicionar horário</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
      <div className="flex justify-end"><SaveBtn onClick={save} loading={loading} tid="svc-save" /></div>
    </div>
  );
}
function Bulk({ children, onClick, tid }) {
  return <button onClick={onClick} data-testid={tid} className="text-xs font-semibold bg-white/5 hover:bg-white/10 px-3 py-2 rounded-full">{children}</button>;
}

// ---------- Meu Link ----------
export function LinkModule({ rid, data, reload }) {
  const r = data.restaurant;
  const origin = window.location.origin;
  const link = `${origin}/${r.slug}`;
  const [slug, setSlug] = useState(r.slug);
  const [saving, setSaving] = useState(false);
  const qrRef = useRef(null);
  const saveSlug = async () => {
    const s = slugify(slug);
    if (!s) return toast.error("Link inválido. Use letras e números.");
    setSaving(true);
    try { await api.put(`/restaurants/${rid}`, { slug: s }); toast.success("Link atualizado"); reload && reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const downloadPng = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a"); a.href = canvas.toDataURL("image/png"); a.download = `qrcode-${r.slug}.png`; a.click();
    toast.success("QR Code baixado");
  };
  const printQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const w = window.open("");
    w.document.write(`<img src="${canvas.toDataURL("image/png")}" style="width:320px"/><p style="font-family:sans-serif;text-align:center">${r.name}</p>`);
    w.document.close(); w.focus(); w.print();
  };
  const share = () => { navigator.share ? navigator.share({ title: r.name, url: link }).catch(() => {}) : navigator.clipboard.writeText(link).then(() => toast.success("Link copiado")); };
  return (
    <div className="space-y-5" data-testid="link-module">
      <Card title="Personalizar link" desc="Escolha o endereço público do seu cardápio. Deve ser único.">
        <div className="flex items-center rounded-xl bg-white/[0.04] border border-white/10 overflow-hidden focus-within:border-[#FF5A5F]">
          <span className="pl-3 text-sm text-zinc-500 whitespace-nowrap">{origin.replace(/^https?:\/\//, "")}/</span>
          <input value={slug} onChange={(e) => setSlug(slugify(e.target.value))} data-testid="link-slug" className="flex-1 bg-transparent px-1 py-2.5 outline-none text-sm min-w-0" />
        </div>
        <div className="flex justify-end mt-3"><SaveBtn onClick={saveSlug} loading={saving} tid="link-slug-save">Salvar link</SaveBtn></div>
      </Card>
      <Card title="Seu link público">
        <input readOnly value={link} data-testid="link-url" className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-4 py-2.5 text-sm text-zinc-300" />
        <div className="flex flex-wrap gap-2 mt-3">
          <LinkBtn onClick={() => window.open(link, "_blank")} tid="link-open"><ExternalLink size={15} /> Abrir</LinkBtn>
          <LinkBtn onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copiado"); }} tid="link-copy"><Copy size={15} /> Copiar</LinkBtn>
          <LinkBtn onClick={share} tid="link-share"><Share2 size={15} /> Compartilhar</LinkBtn>
        </div>
      </Card>
      <Card title="QR Code" desc="Aponta sempre para o link atual do restaurante. Ideal para mesas, balcão e bio.">
        <div className="flex flex-col items-center gap-4">
          <div ref={qrRef} className="bg-white p-4 rounded-2xl" data-testid="qr-canvas"><QRCodeCanvas value={link} size={200} level="H" /></div>
          <div className="flex flex-wrap gap-2 justify-center">
            <button onClick={downloadPng} data-testid="qr-download" className="flex items-center gap-2 rounded-full hmmm-gradient px-5 py-2.5 font-bold hmmm-glow text-white"><Download size={16} /> Baixar PNG</button>
            <button onClick={printQr} data-testid="qr-print" className="flex items-center gap-2 rounded-full bg-white/10 px-5 py-2.5 font-bold"><Printer size={16} /> Imprimir</button>
          </div>
        </div>
      </Card>
    </div>
  );
}
function LinkBtn({ children, onClick, tid }) {
  return <button onClick={onClick} data-testid={tid} className="flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 px-4 py-2.5 text-sm font-semibold">{children}</button>;
}

// ---------- Minha Conta ----------
export function AccountModule({ data }) {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const changePw = async () => {
    if (pw.next.length < 6) return toast.error("A nova senha deve ter ao menos 6 caracteres");
    if (pw.next !== pw.confirm) return toast.error("As senhas não coincidem");
    setLoading(true);
    try { await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next }); toast.success("Senha alterada com sucesso"); setPw({ current: "", next: "", confirm: "" }); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  return (
    <div className="space-y-5" data-testid="account-module">
      <Card title="Dados da conta">
        <div className="space-y-3">
          <Field label="Nome" value={name} onChange={setName} tid="acc-name" />
          <Field label="E-mail" value={user?.email} onChange={() => {}} tid="acc-email" />
          <div>
            <label className="text-xs font-semibold text-zinc-400">Restaurante vinculado</label>
            <input readOnly value={data.restaurant.name} data-testid="acc-restaurant" className="w-full mt-1 rounded-xl bg-white/[0.02] border border-white/10 px-4 py-2.5 text-sm text-zinc-500" />
          </div>
        </div>
      </Card>
      <Card title="Alterar senha">
        <div className="space-y-3 max-w-sm">
          <PwField label="Senha atual" value={pw.current} onChange={(v) => setPw((p) => ({ ...p, current: v }))} show={show} tid="acc-current" />
          <PwField label="Nova senha" value={pw.next} onChange={(v) => setPw((p) => ({ ...p, next: v }))} show={show} tid="acc-new" />
          <PwField label="Confirmar nova senha" value={pw.confirm} onChange={(v) => setPw((p) => ({ ...p, confirm: v }))} show={show} tid="acc-confirm" />
          <button type="button" onClick={() => setShow(!show)} className="flex items-center gap-1.5 text-xs text-zinc-400">{show ? <EyeOff size={14} /> : <Eye size={14} />} {show ? "Ocultar" : "Mostrar"} senhas</button>
          <SaveBtn onClick={changePw} loading={loading} tid="acc-save">Alterar senha</SaveBtn>
        </div>
      </Card>
    </div>
  );
}
function PwField({ label, value, onChange, show, tid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-zinc-400">{label}</label>
      <input type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} data-testid={tid}
        className="w-full mt-1 rounded-xl bg-white/[0.04] border border-white/10 px-4 py-2.5 outline-none text-sm focus:border-[#FF5A5F]" />
    </div>
  );
}
